import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import FlexSearch from 'flexsearch';

export const SearchContext = React.createContext({
    query: ``,
    searchResults: [] as SearchResult[],
    error: false,
    searchValid: false,
    searchIndex: undefined,
    searchStore: undefined,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    handleSearch: (query: string) => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    loadSearch: () => {},
    categorizedResults: [] as CategorizedResult[],
    popularSearchTerms: [] as string[],
});

export interface SearchResult {
    title: string;
    url: string;
    date: Date;
    type: string;
    id: number;
    externalLink?: boolean;
    score?: number;
}

export interface CategorizedResult {
    heading: string;
    pages: {
        title: string;
        url: string;
        date: Date;
        externalLink?: boolean;
        score?: number;
    }[];
}

export const SearchContextProvider = ({ children }) => {
    const MIN_SEARCH_LENGTH = 3;

    const data = useStaticQuery(graphql`
        query {
            localSearchPages {
                publicIndexURL
                publicStoreURL
            }
            allTaxonomyTermPopularSearchTerms {
                nodes {
                    name
                }
            }
        }
    `);

    const popularSearchTerms =
        data.allTaxonomyTermPopularSearchTerms?.nodes?.map((n) => n.name);

    const [query, setQuery] = useState(``);
    const [searchError, setSearchError] = useState(false);
    const [searchValid, setSearchValid] = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

    const [searchIndex, setSearchIndex] = useState(null);
    const [searchStore, setSearchStore] = useState(null);

    const [categorizedResults, setCategorizedResults] = useState<
        CategorizedResult[]
    >([]);

    const flexsearch = useFlexSearch(query, searchIndex, searchStore);

    const handleSearch = (text: string) => {
        setQuery(text);
    };

    useEffect(() => {
        if (
            !searchIndex ||
            !searchStore ||
            !query ||
            query.trim().length < MIN_SEARCH_LENGTH ||
            searchError
        ) {
            setSearchValid(false);
            setSearchResults([]);
            return;
        }
        setSearchValid(true);
        setSearchResults(flexsearch);
    }, [query, searchIndex, searchStore, searchError]);

    useEffect(() => {
        const articles: CategorizedResult = {
            heading: `Articles`,
            pages: [],
        };

        const caseStudies: CategorizedResult = {
            heading: `Case Studies`,
            pages: [],
        };

        const newsPress: CategorizedResult = {
            heading: `News & Press`,
            pages: [],
        };

        const markets: CategorizedResult = {
            heading: `Markets`,
            pages: [],
        };
        const pages: CategorizedResult = {
            heading: `Pages`,
            pages: [],
        };

        for (const result of searchResults) {
            let target = pages;

            if (result?.type === `node__article`) {
                target = articles;
            } else if (result?.type === `node__case_study_page`) {
                target = caseStudies;
            } else if (result?.type === `node__city_page`) {
                target = markets;
            } else if (result?.type === `node__news_press_article`) {
                target = newsPress;
            }

            target.pages.push({
                title: result?.title,
                url: result?.url,
                date: new Date(result?.date),
                externalLink: result?.externalLink,
                score: result?.score
            });
        }

        const newCategorizedResults: CategorizedResult[] = [];
        if (pages.pages.length) {
            newCategorizedResults.push(pages);
        }
        if (articles.pages.length) {
            newCategorizedResults.push(articles);
        }
        if (newsPress.pages.length) {
            newCategorizedResults.push(newsPress);
        }
        if (caseStudies.pages.length) {
            newCategorizedResults.push(caseStudies);
        }
        if (markets.pages.length) {
            newCategorizedResults.push(markets);
        }

        for (const item of newCategorizedResults) {
            // Sort time-sensitive content by date, everything else by relevance
            if (item.heading === `Articles` || item.heading === `News & Press`) {
                item.pages.sort((a, b) => b.date.valueOf() - a.date.valueOf());
            } else {
                item.pages.sort((a, b) => (b.score || 0) - (a.score || 0));
            }
        }

        setCategorizedResults(newCategorizedResults);
    }, [searchResults]);

    const loadSearch = async () => {
        if (searchIndex && searchStore) return;
        try {
            const indexUrl = data.localSearchPages.publicIndexURL;
            const publicStoreURL = data.localSearchPages.publicStoreURL;
            const result = await Promise.all([
                axios.get(indexUrl),
                axios.get(publicStoreURL),
            ]);

            setSearchIndex(result[0].data);
            setSearchStore(result[1].data);
            handleSearch(query);
        } catch (e) {
            console.error(e);
            setSearchError(true);
        }
    };

    return (
        <SearchContext.Provider
            value={{
                query,
                handleSearch,
                loadSearch,
                error: searchError,
                searchIndex,
                searchStore,
                searchResults,
                searchValid,
                categorizedResults,
                popularSearchTerms,
            }}
        >
            {children}
        </SearchContext.Provider>
    );
};

export const useSearchContext = () => useContext(SearchContext);

export const useFlexSearch = (query, providedIndex, store, searchOptions?) => {
    const [index, setIndex] = useState(null);

    useEffect(() => {
        if (!providedIndex) {
            setIndex(null);
            return;
        }

        if (providedIndex instanceof FlexSearch) {
            setIndex(providedIndex);
            return;
        }

        const importedIndex = FlexSearch.create({
            tokenize: `full`,
        });

        (importedIndex.import as any)(providedIndex, { serialize: false });

        setIndex(importedIndex);
    }, [providedIndex]);

    return useMemo(() => {
        if (query === undefined || query === null || !index || !store)
            return [];

        const words = query.trim().split(/\s+/);
        if (words.length === 0) return [];

        const results = index.search(query, {
            limit: 100,
            suggest: true,
            ...searchOptions,
        });

        // Deduplicate IDs from FlexSearch
        const uniqueResults = [...new Set(results)];

        // Custom scoring: boost results where query words appear in title or URL
        const scoredResults = uniqueResults.map((id) => {
            const item = store[id];
            if (!item) return null;

            let score = 1;
            const titleLower = item.title?.toLowerCase() || ``;
            const urlLower = item.url?.toLowerCase() || ``;
            const queryLower = query.toLowerCase();
            
            const normalizeText = (text) => text.replace(/[\s-]+/g, ``);
            const normalizedQuery = normalizeText(queryLower);
            const normalizedTitle = normalizeText(titleLower);
            const normalizedUrl = normalizeText(urlLower);

            if (titleLower === queryLower) {
                score += 10000;
            } else if (titleLower.includes(queryLower)) {
                score += 5000;
            }
            
            if (normalizedTitle === normalizedQuery) {
                score += 4000;
            } else if (normalizedTitle.includes(normalizedQuery)) {
                score += 2000;
            }

            // URL slug match (e.g., "sports-advertising" matches "sports advertising")
            const slugQuery = queryLower.replace(/\s+/g, `-`);
            if (urlLower.includes(`/${slugQuery}`)) {
                score += 1500;
            } else if (urlLower.includes(slugQuery)) {
                score += 1000;
            }
            
            // Normalized URL match
            if (normalizedUrl.includes(normalizedQuery)) {
                score += 800;
            }

            // Title starts with query
            if (titleLower.startsWith(queryLower)) {
                score += 3000;
            }

            // Count how many query words appear in title (in order)
            let wordMatchScore = 0;
            words.forEach((word, idx) => {
                const wordLower = word.toLowerCase();
                const wordIndex = titleLower.indexOf(wordLower);
                if (wordIndex !== -1) {
                    wordMatchScore += 100;
                    // Bonus for words appearing in order
                    if (idx > 0) {
                        const prevWordIndex = titleLower.indexOf(words[idx - 1].toLowerCase());
                        if (prevWordIndex !== -1 && wordIndex > prevWordIndex) {
                            wordMatchScore += 50;
                        }
                    }
                }
            });
            score += wordMatchScore;

            // Boost product pages
            if (item.type === `node__product_page`) {
                score += 200;
            } else if (item.type === `node__landing_page`) {
                score += 50;
            }

            return { ...item, score };
        }).filter(Boolean);

        // Filter out results with score 0
        const filtered = scoredResults.filter((item) => item.score > 0);

        // Sort by score descending
        filtered.sort((a, b) => b.score - a.score);

        // Limit to top 20 results
        return filtered.slice(0, 20);
    }, [query, index, store]);
};
